import React, { useEffect, useState } from 'react';
import SummaryApi from '../common';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import Modal from 'react-modal';

// ────────────────────────────────────────────────
// THEME — same rules as other files
// ────────────────────────────────────────────────
const THEME = {
  primary: 'rgb(23, 44, 87)',        // #172C57
  primaryLight: 'rgba(23, 44, 87, 0.08)',
  actionBlue: '#2563EB',
  actionBlueHover: '#1D4ED8',
  actionGreen: '#10B981',
  actionGreenHover: '#059669',
  actionRed: '#EF4444',
};

const EmployeeCallLogs = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalCurrentPage, setModalCurrentPage] = useState(1);
  const [minDate, setMinDate] = useState('');

  useEffect(() => {
    fetchCallLogs();
    const today = new Date().toISOString().split('T')[0];
    setSelectedDate(today);
  }, []);

  const filteredRows = rows.filter((row) => {
    const dateFilter = selectedDate
      ? new Date(row.created_date).toISOString().split('T')[0] === selectedDate
      : true;

    const searchFilter = searchTerm
      ? row.employee_name && row.employee_name.toLowerCase().includes(searchTerm.toLowerCase())
      : true;

    return dateFilter && searchFilter;
  });

  const getEmployeeTaskCount = (employeeName) => {
    return filteredRows.filter(
      (row) => row.employee_name === employeeName && new Date(row.created_date).toISOString().split('T')[0] === selectedDate
    ).length;
  };

  const uniqueEmployeeRows = Array.from(
    new Set(rows.map((row) => row.employee_name))
  ).map((employeeName) => rows.find((row) => row.employee_name === employeeName));

  const fetchCallLogs = async () => {
    setLoading(true);
    try {
      const response = await fetch(SummaryApi.getcalllogsstudent.url, {
        method: SummaryApi.getcalllogsstudent.method,
        headers: {
          'Authorization': 'Bearer sandeep_university_916',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const responseData = await response.json();
      if (responseData.status && responseData.data.length > 0) {
        setRows(responseData.data);

        const earliestDate = responseData.data
          .map(row => new Date(row.created_date))
          .sort((a, b) => a - b)[0]
          .toISOString()
          .split('T')[0];
        setMinDate(earliestDate);
      } else {
        toast.info('No call logs found.');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch call logs.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExcel1 = () => {
    if (!selectedEmployee) return;

    const allEmployeeRows = filteredRows.filter(
      row => selectedEmployee && row.employee_name === selectedEmployee.employee_name &&
        new Date(row.created_date).toISOString().split('T')[0] === selectedDate
    );

    if (allEmployeeRows.length === 0) {
      const headers = ['S.No', 'Student Name', 'In Time', 'Out Time', 'Remarks'];
      const dataForExcel = [headers];
      const worksheet = XLSX.utils.aoa_to_sheet(dataForExcel);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Task Logs');
      XLSX.writeFile(workbook, `${selectedEmployee.employee_name}_Task_Logs_${selectedDate}.xlsx`);
      return;
    }

    const dataForExcel = allEmployeeRows.map((row, index) => ({
      'S.No': index + 1,
      'Student Name': row.student_name || 'N/A',
      'In Time': formatTime(row.in_time) || 'N/A',
      'Out Time': formatTime(row.out_time) || 'N/A',
      'Remarks': row.remarks || 'No remarks'
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Task Logs');
    XLSX.writeFile(workbook, `${selectedEmployee.employee_name}_Task_Logs_${selectedDate}.xlsx`);
  };

  const isDownloadDisabled = !selectedEmployee || filteredRows.filter(
    row => row.employee_name === selectedEmployee.employee_name &&
      new Date(row.created_date).toISOString().split('T')[0] === selectedDate
  ).length === 0;

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const handleNextPage = () => {
    if (currentPage < Math.ceil(filteredRows.length / rowsPerPage)) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleFirstPage = () => setCurrentPage(1);

  const handleLastPage = () => {
    setCurrentPage(Math.ceil(filteredRows.length / rowsPerPage));
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleDateChange = (event) => {
    setSelectedDate(event.target.value);
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  };

  const formatTime = (timeString) => {
    if (timeString === "0000-00-00 00:00:00" || isNaN(new Date(timeString).getTime())) return "N/A";
    return formatDate(timeString);
  };

  const paginatedRows = filteredRows.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const handleTaskClick = (employee) => {
    setSelectedEmployee(employee);
    setModalIsOpen(true);
    setModalCurrentPage(1);
  };

  const modalFilteredRows = selectedEmployee
    ? rows.filter(row => row.employee_name === selectedEmployee.employee_name && new Date(row.created_date).toISOString().split('T')[0] === selectedDate)
    : [];

  const modalPaginatedRows = modalFilteredRows.slice(
    (modalCurrentPage - 1) * rowsPerPage,
    modalCurrentPage * rowsPerPage
  );

  const handleModalNextPage = () => {
    if (modalFilteredRows.length > 0 && modalCurrentPage < Math.ceil(modalFilteredRows.length / rowsPerPage)) {
      setModalCurrentPage(modalCurrentPage + 1);
    }
  };

  const handleModalPrevPage = () => {
    if (modalFilteredRows.length > 0 && modalCurrentPage > 1) {
      setModalCurrentPage(modalCurrentPage - 1);
    }
  };

  const handleModalFirstPage = () => {
    if (modalFilteredRows.length > 0) {
      setModalCurrentPage(1);
    }
  };

  const handleModalLastPage = () => {
    if (modalFilteredRows.length > 0) {
      setModalCurrentPage(Math.ceil(modalFilteredRows.length / rowsPerPage));
    }
  };

  const isNoDataAvailable = modalFilteredRows.length === 0;
  const modalPaginationDisabled = isNoDataAvailable ? true : false;

  return (
    <div className="container mx-auto">
      <header className="bg-white rounded-xl border border-slate-200 shadow-sm px-6 py-4 mb-5">
        <h1
          className="text-xl md:text-2xl font-bold text-center"
          style={{ color: THEME.primary }}
        >
          Employee Call Logs
        </h1>
        <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-3">
          <div className="flex gap-4">
            <input
              type="date"
              className="border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#172C57]"
              value={selectedDate}
              onChange={handleDateChange}
              min={minDate}
              max={new Date().toISOString().split('T')[0]}
            />
          </div>
          <div
            className="text-sm font-semibold"
            style={{ color: THEME.primary }}
          >
            {selectedDate === new Date().toISOString().split('T')[0]
              ? `Today's Employee Count: ${uniqueEmployeeRows.length}`
              : `Employee Count : ${uniqueEmployeeRows.length}`}
          </div>
        </div>
      </header>

      <table id="call-log-table" className="min-w-full mb-4 rounded-lg shadow-md overflow-hidden">
        <thead className="bg-gray-200">
          <tr>
            <th className="text-center py-3 px-4 font-semibold whitespace-nowrap">S.NO</th>
            <th className="text-center py-3 px-4 font-semibold whitespace-nowrap">Employee Name</th>
            <th className="text-center py-3 px-4 font-semibold whitespace-nowrap">Task Count</th>
            <th className="text-center py-3 px-4 font-semibold whitespace-nowrap">Status</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="4" className="text-center py-4">Loading...</td>
            </tr>
          ) : uniqueEmployeeRows.length > 0 ? (
            uniqueEmployeeRows.map((row, index) => {
              const taskCount = getEmployeeTaskCount(row.employee_name);
              return (
                <tr key={index} className="hover:bg-slate-50 transition-colors">
                  <td className="text-center py-2 px-4 text-xs text-slate-600">{index + 1}</td>
                  <td className="text-center py-2 px-4 text-xs font-medium text-slate-800">
                    {row.employee_name || "N/A"}
                  </td>
                  <td
                    className="text-center py-2 px-4 text-xs cursor-pointer transition-all duration-200"
                    onClick={() => handleTaskClick(row)}
                  >
                    <span
                      className="px-2.5 py-1 rounded-full font-semibold"
                      style={{
                        backgroundColor: THEME.primaryLight,
                        color: THEME.primary,
                      }}
                    >
                      {taskCount}
                    </span>
                  </td>
                  <td className={`text-center py-2 px-4 text-xs font-semibold ${row.is_active === "1" ? 'text-green-600' : 'text-red-500'}`}>
                    {row.is_active === "1" ? "Active" : "Inactive"}
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan="4" className="text-center py-4">No data available</td>
            </tr>
          )}
        </tbody>
      </table>

      <footer className="text-center py-2 mt-4 rounded-lg">
        <div className="flex items-center justify-between mt-4">
          <div>
            <label
              htmlFor="rowsPerPage"
              className="mr-2 font-semibold"
              style={{ color: THEME.primary }}
            >
              Rows per page:
            </label>
            <select
              id="rowsPerPage"
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
              style={{ color: THEME.primary }}
              className="border rounded px-2 py-1 cursor-pointer"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </footer>

      {/* Modal for Employee Task Details */}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={() => setModalIsOpen(false)}
        contentLabel="Employee Task Details"
        className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-2xl max-w-4xl w-[95%] max-h-[85vh] overflow-y-auto z-50 outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 z-40"
      >
        {/* Sticky Header */}
        <div className="sticky top-0 z-10 bg-white px-6 py-4 border-b border-slate-100">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p
              className="text-sm font-semibold"
              style={{ color: THEME.primary }}
            >
              Task Date : {new Date(selectedDate).toLocaleDateString('en-GB')}
            </p>

            <h2
              className="text-lg font-bold text-center flex-1"
              style={{ color: THEME.primary }}
            >
              {selectedEmployee?.employee_name
                ? `${selectedEmployee.employee_name}'s Task Details`
                : 'No Employee Selected'}
            </h2>

            <div className="flex items-center gap-2">
              <button
                onClick={handleDownloadExcel1}
                disabled={isDownloadDisabled}
                style={{
                  backgroundColor: isDownloadDisabled ? '#D1D5DB' : THEME.actionGreen,
                  cursor: isDownloadDisabled ? 'not-allowed' : 'pointer',
                }}
                onMouseEnter={(e) => {
                  if (!isDownloadDisabled)
                    e.currentTarget.style.backgroundColor = THEME.actionGreenHover;
                }}
                onMouseLeave={(e) => {
                  if (!isDownloadDisabled)
                    e.currentTarget.style.backgroundColor = THEME.actionGreen;
                }}
                className="text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap shadow-sm"
              >
                Download Excel
              </button>
              <button
                onClick={() => setModalIsOpen(false)}
                className="text-gray-500 hover:text-gray-800 transition-colors p-1"
                aria-label="Close"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                  className="w-5 h-5"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Table Content */}
        <div className="overflow-x-auto px-6 py-4">
          <table className="min-w-full rounded-lg shadow-md overflow-hidden">
            <thead className="bg-gray-200">
              <tr>
                <th className="text-center py-2 px-4 font-semibold text-sm whitespace-nowrap">S.No</th>
                <th className="text-center py-2 px-4 font-semibold text-sm whitespace-nowrap">Student Name</th>
                <th className="text-center py-2 px-4 font-semibold text-sm whitespace-nowrap">In Time</th>
                <th className="text-center py-2 px-4 font-semibold text-sm whitespace-nowrap">Out Time</th>
                <th className="text-center py-2 px-4 font-semibold text-sm whitespace-nowrap">Remarks</th>
              </tr>
            </thead>
            <tbody>
              {modalPaginatedRows.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-6 text-gray-500">
                    No records available
                  </td>
                </tr>
              ) : (
                modalPaginatedRows.map((row, index) => (
                  <tr key={index} className="hover:bg-slate-50 transition-colors">
                    <td className="text-center py-2 px-4 text-sm">{(modalCurrentPage - 1) * rowsPerPage + index + 1}</td>
                    <td className="text-center py-2 px-4 text-sm">{row.student_name || "N/A"}</td>
                    <td className="text-center py-2 px-4 text-sm">{formatTime(row.in_time)}</td>
                    <td className="text-center py-2 px-4 text-sm">{formatTime(row.out_time)}</td>
                    <td className="text-center py-2 px-4 text-sm">{row.remarks || "No remarks"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer with Total Records and Pagination */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-3 px-6 py-4 border-t border-slate-100">
          <div className="text-sm font-medium" style={{ color: THEME.primary }}>
            Total Records: {modalFilteredRows.length} | Page {modalCurrentPage} of {Math.ceil(modalFilteredRows.length / rowsPerPage) || 1}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleModalFirstPage}
              disabled={modalPaginationDisabled || modalCurrentPage === 1}
              style={{ color: THEME.primary }}
              className="border border-gray-300 px-3 py-1 rounded-lg text-xs font-semibold hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              First
            </button>
            <button
              onClick={handleModalPrevPage}
              disabled={modalPaginationDisabled || modalCurrentPage === 1}
              style={{ color: THEME.primary }}
              className="border border-gray-300 px-3 py-1 rounded-lg text-xs font-semibold hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <button
              onClick={handleModalNextPage}
              disabled={modalPaginationDisabled || modalCurrentPage === Math.ceil(modalFilteredRows.length / rowsPerPage)}
              style={{ color: THEME.primary }}
              className="border border-gray-300 px-3 py-1 rounded-lg text-xs font-semibold hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
            <button
              onClick={handleModalLastPage}
              disabled={modalPaginationDisabled || modalCurrentPage === Math.ceil(modalFilteredRows.length / rowsPerPage)}
              style={{ color: THEME.primary }}
              className="border border-gray-300 px-3 py-1 rounded-lg text-xs font-semibold hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Last
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EmployeeCallLogs;